/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */
import { Fragment, useState, useCallback } from 'react'
import { css, jsx } from '@emotion/react'
import { debounce, union, difference } from 'lodash'
import { defineMessages, useIntl } from 'react-intl'

import { EuiBadge, EuiIcon, EuiSpacer, useEuiTheme } from '@elastic/eui'

import type { SearchRequest } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, AllocatorSearchResult } from '@modules/ui-types'

import Typeahead from '../../../Typeahead'

import ShowAllocator from './ShowAllocator'
import createAllocatorQuery from './createAllocatorQuery'

export interface Props {
  searchAllocators: (query: SearchRequest) => void
  searchAllocatorsRequest: AsyncRequestState
  searchResults?: AllocatorSearchResult[]
  regionId: string
  allocatorId: string
  selectedAllocatorIds: string[]
  unselectAllocatorId: (allocatorId: string) => void
  selectAllocatorId: (allocatorId: string) => void
  placeholder?: string
}

// We don't want to search on every key press,
// so we only search if there have been query
// updates within a certain interval
const onSearch = debounce((cb) => cb(), 100)

const SearchAllocators = ({
  placeholder,
  selectedAllocatorIds,
  searchResults,
  searchAllocators,
  allocatorId,
  unselectAllocatorId,
  selectAllocatorId,
}: Props) => {
  const [q, setQ] = useState('')
  const [isBidingTime, setIsBidingTime] = useState(false)
  const { euiTheme } = useEuiTheme()
  const { formatMessage } = useIntl()

  const messages = defineMessages({
    remove: {
      id: `search-allocators.remove`,
      defaultMessage: `Remove`,
    },
  })

  const allocatorResultStyle = css({
    display: 'block',
    padding: '0.625rem',
    cursor: 'pointer',
    color: euiTheme.colors.primary,
    '&:hover': {
      backgroundColor: euiTheme.colors.lightestShade,
    },
  })

  const search = useCallback(
    (query: string, selectedIds: string[]) => {
      setIsBidingTime(true)

      onSearch(() => {
        setIsBidingTime(false)
        setQ(query)

        if (query.trim() === ``) {
          return
        }

        searchAllocators(
          createAllocatorQuery({
            query,
            excludedAllocatorIds: [...selectedIds, allocatorId],
          }),
        )
      })
    },
    [searchAllocators, allocatorId],
  )

  const unselectAllocator = useCallback(
    (targetId: string) => {
      unselectAllocatorId(targetId)
      search(q, difference(selectedAllocatorIds, [targetId]))
    },
    [q, selectedAllocatorIds, unselectAllocatorId, search],
  )

  const goTo = useCallback(
    (allocator: AllocatorSearchResult) => {
      search(q, union(selectedAllocatorIds, [allocator.id]))
      selectAllocatorId(allocator.id)
    },
    [q, selectedAllocatorIds, selectAllocatorId, search],
  )

  return (
    <div>
      <div>
        <Typeahead<AllocatorSearchResult>
          icon='search'
          isBidingTime={isBidingTime}
          q={q}
          search={(qq) => search(qq, selectedAllocatorIds)}
          goTo={goTo}
          placeholder={placeholder}
          results={searchResults && { record: searchResults, isSearching: false }}
        >
          {(allocator: AllocatorSearchResult) => (
            <div onClick={() => goTo(allocator)} css={allocatorResultStyle}>
              <ShowAllocator allocatorId={allocator.id} />
            </div>
          )}
        </Typeahead>
      </div>

      {selectedAllocatorIds.length > 0 && (
        <Fragment>
          <EuiSpacer size='s' />

          <div data-test-id='vacateOption-selectedAllocatorIds'>
            {selectedAllocatorIds.map((allocatorId) => (
              <EuiBadge
                key={allocatorId}
                onClick={() => unselectAllocator(allocatorId)}
                onClickAriaLabel={formatMessage(messages.remove)}
              >
                {allocatorId}

                {` `}

                <EuiIcon type='cross' />
              </EuiBadge>
            ))}
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default SearchAllocators
